import type {
  AdminRouteStyle, ApiErrorBody, AvoidedZone, BreakRequest, Category, CategoryGroup, City, CoordStop, CoveragePoint, DirectionsLeg, DuplicateCandidate, HealthReport, IpsAssessment, Job, ManualPoi, PlaceDetails, PlaceRef, PlaceTally, PlanLeg, PlanRequest, PlanStop, Poi, PoiCoverage, ProminenceBias, RouteAlternative, RoutePlan, RouteStyle, RouteTheme, SavedRoute, SavedRouteSummary, SavedTrip, SavedTripSummary, StopKind, StopRecommendation, SystemStatus, ThemeOutcome, TransitLegDetail, TravelMode, TripDay, TripPlan, TripRequest,
} from './types';
import type { ViewBox } from '../util/viewBox';
import type { City as PbCity } from '../proto/travplaces/v1/city_pb';
import type { Category as PbCategory } from '../proto/travplaces/v1/category_pb';
import type { Poi as PbPoi } from '../proto/travplaces/v1/poi_pb';
import type { RouteStyle as PbRouteStyle } from '../proto/travplaces/v1/route_style_pb';
import type { RouteTheme as PbRouteTheme } from '../proto/travplaces/v1/route_theme_pb';
import type { CategoryGroup as PbCategoryGroup } from '../proto/travplaces/v1/category_group_pb';
import type { MessageInitShape } from '@bufbuild/protobuf';

import { apiBaseUrl } from './origin';
import { accessToken } from './session';
import { timestampDate } from '@bufbuild/protobuf/wkt';
import { rpc, transport } from './transport';
import {
  plan as planMethod,
  planCoverage as planCoverageMethod,
  planDirections as planDirectionsMethod,
  planRecommendations as planRecommendationsMethod,
  planThemes as planThemesMethod,
} from '../proto/travplaces/v1/planning-PlanningService_connectquery';
import {
  PlanRequestSchema, PlaceRefSchema, CoordStopSchema, BreakRequestSchema,
} from '../proto/travplaces/v1/planning_pb';
import type {
  RoutePlan as PbRoutePlan,
  RouteStop as PbRouteStop,
  RouteLeg as PbRouteLeg,
  TransitLegDetail as PbTransitLegDetail,
  AvoidedZone as PbAvoidedZone,
  RouteAlternative as PbRouteAlternative,
  ThemeOutcome as PbThemeOutcome,
  IpsAssessment as PbIpsAssessment,
  StopRecommendation as PbStopRecommendation,
} from '../proto/travplaces/v1/planning_pb';
import { listCities, getCity } from '../proto/travplaces/v1/city-CityService_connectquery';
import { listCategories } from '../proto/travplaces/v1/category-CategoryService_connectquery';
import { listCategoryGroups } from '../proto/travplaces/v1/category_group-CategoryGroupService_connectquery';
import { listRouteStyles } from '../proto/travplaces/v1/route_style-RouteStyleService_connectquery';
import { listRouteThemes } from '../proto/travplaces/v1/route_theme-RouteThemeService_connectquery';
import { listSettings } from '../proto/travplaces/v1/settings-SettingsService_connectquery';
import { searchPois as searchPoisMethod } from '../proto/travplaces/v1/poi_search-PoiSearchService_connectquery';
import { getPoiGeojson } from '../proto/travplaces/v1/poi_map-PoiMapService_connectquery';
import { getPoi, getPoiDetails } from '../proto/travplaces/v1/poi_detail-PoiDetailService_connectquery';
import { getCityRegions, citiesOnRoute as citiesOnRouteMethod } from '../proto/travplaces/v1/city_geo-CityGeoService_connectquery';
import { listCityCategories } from '../proto/travplaces/v1/city_categories-CityCategoryService_connectquery';
import type { CityCategory as PbCityCategory } from '../proto/travplaces/v1/city_categories_pb';
import {
  listSavedRoutes as listSavedRoutesMethod,
  getSavedRoute as getSavedRouteMethod,
  saveRoute as saveRouteMethod,
  updateSavedRoute as updateSavedRouteMethod,
  deleteSavedRoute as deleteSavedRouteMethod,
  poiCoverage as poiCoverageMethod,
  placeTallies as placeTalliesMethod,
} from '../proto/travplaces/v1/saved_route-SavedRouteService_connectquery';
import type {
  SavedRoute as PbSavedRoute,
  SavedRouteSummary as PbSavedRouteSummary,
} from '../proto/travplaces/v1/saved_route_pb';
import {
  listTrips as listTripsMethod,
  getTrip as getTripMethod,
  saveTrip as saveTripMethod,
  updateTrip as updateTripMethod,
  deleteTrip as deleteTripMethod,
  planTrip as planTripMethod,
  planTripDay as planTripDayMethod,
} from '../proto/travplaces/v1/trip-TripService_connectquery';
import type {
  Trip as PbTrip,
  TripSummary as PbTripSummary,
} from '../proto/travplaces/v1/trip_pb';
import type { Timestamp } from '@bufbuild/protobuf/wkt';
// ── admin: Connect endpoints ────────────────────────────────────────
import {
  listAllCities,
  createCity as createCityMethod,
  updateCity as updateCityMethod,
  deleteCity as deleteCityMethod,
  unpublishCity as unpublishCityMethod,
  fetchBoundary as fetchBoundaryMethod,
  systemStatus as systemStatusMethod,
} from '../proto/travplaces/v1/admin_city-AdminCityService_connectquery';
import {
  setSetting as setSettingMethod,
  restartEngine as restartEngineMethod,
  testExternalSearch as testExternalSearchMethod,
} from '../proto/travplaces/v1/admin_settings-AdminSettingsService_connectquery';
import {
  createRouteStyle as createRouteStyleMethod,
  updateRouteStyle as updateRouteStyleMethod,
  deleteRouteStyle as deleteRouteStyleMethod,
  createTheme as createThemeMethod,
  updateTheme as updateThemeMethod,
  deleteTheme as deleteThemeMethod,
  createCategory as createCategoryMethod,
  updateCategory as updateCategoryMethod,
  deleteCategory as deleteCategoryMethod,
  createCategoryGroup as createCategoryGroupMethod,
  updateCategoryGroup as updateCategoryGroupMethod,
  deleteCategoryGroup as deleteCategoryGroupMethod,
} from '../proto/travplaces/v1/admin_taxonomy-AdminTaxonomyService_connectquery';
import {
  listAdminPois,
  poiCounts as poiCountsMethod,
  createPoi as createPoiMethod,
  updatePoi as updatePoiMethod,
  deletePoi as deletePoiMethod,
  poiDuplicates as poiDuplicatesMethod,
} from '../proto/travplaces/v1/admin_poi-AdminPoiService_connectquery';
import {
  listJobs,
  getJob,
  cancelJob as cancelJobMethod,
  enqueueImportOsm,
  enqueueImportPois,
  enqueueImportFsq,
  enqueueBuildValhalla,
  enqueueBuildOtp,
  enqueueBuildBasemap,
  enqueueVerify,
} from '../proto/travplaces/v1/job-JobService_connectquery';
import type { ManualPoi as PbManualPoi } from '../proto/travplaces/v1/admin_poi_pb';
import type { Job as PbJob } from '../proto/travplaces/v1/job_pb';
import {
  searchExternal as searchExternalMethod,
  describePlace as describePlaceMethod,
} from '../proto/travplaces/v1/places-PlacesService_connectquery';
import {
  geocode as geocodeMethod,
  searchPlacesToAdd as searchPlacesToAddMethod,
} from '../proto/travplaces/v1/places-AdminGeocodeService_connectquery';

// ── Connect → legacy type mappers ───────────────────────────────────
//
// The Connect messages are the current backend's shape; these rebuild the
// exact legacy types the components consume, defaulting the fields the proto
// does not (yet) carry so no component needs to change.

function mapCity(c: PbCity): City {
  return {
    id: c.id,
    slug: c.slug,
    name: c.name,
    countryCode: c.countryCode,
    timezone: c.timezone,
    units: (c.units === 'metric' ? 'metric' : 'imperial'),
    status: (c.status as City['status']) || 'ready',
    statusMessage: null,
    center: { lat: c.centerLat, lng: c.centerLng },
    bbox: [c.bboxMinLng, c.bboxMinLat, c.bboxMaxLng, c.bboxMaxLat],
    defaultAvoidDistanceM: c.defaultAvoidDistanceM,
    safetyWeights: {
      hospital: 0,
      police: 0,
      fire_station: 0,
      max_useful_distance_m: 0,
    },
    otpSlot: null,
    poiCount: c.poiCount,
    gtfsFeedEndDate: null,
    hasBasemap: c.hasBasemap,
    hasExtract: c.hasExtract,
    hasRoutingTiles: c.hasRoutingTiles,
    poiSource: 'osm',
    hasBoundary: false,
  };
}

function mapCategory(c: PbCategory): Category {
  return {
    id: c.id,
    key: c.key,
    label: c.label,
    group: c.categoryGroup,
    icon: c.icon || null,
    defaultDwellMinutes: c.defaultDwellMinutes,
    isSafetyInfrastructure: c.isSafetyInfrastructure,
    isAvoidable: c.isAvoidable,
    isVisitable: c.isVisitable,
  };
}

/** Parses provider JSON, treating anything unusable as absent. */
function safeJson(raw: string): unknown | null {
  if (!raw) return null;
  try {
    return JSON.parse(raw) as unknown;
  } catch {
    return null;
  }
}

function mapPoi(p: PbPoi): Poi {
  // Every field the server sends, as the Node API's toPoi returned them. The
  // details dialog reads the category for its chip and compares the key with
  // the provider's type; blank here, it drew an empty chip beside a duplicate.
  return {
    id: p.id,
    cityId: p.cityId,
    cityName: p.cityName || undefined,
    categoryId: p.categoryId,
    categoryKey: p.categoryKey,
    categoryLabel: p.categoryLabel,
    name: p.name,
    lat: p.lat,
    lng: p.lng,
    address: p.address || null,
    description: p.description || null,
    rating: p.rating ?? null,
    openingHours: p.openingHours || null,
    dwellMinutes: p.dwellMinutes,
    source: (p.source as Poi['source']) || 'osm',
    phone: p.phone || null,
    website: p.website || null,
  };
}

function mapRouteStyle(s: PbRouteStyle): RouteStyle {
  return {
    id: s.id,
    key: s.key,
    cityId: s.cityId || null,
    label: s.label,
    icon: s.icon || null,
    description: s.description,
    categoryKeys: s.categoryKeys,
    boostCategoryKeys: s.boostCategoryKeys,
    prominenceBias: (s.prominenceBias as ProminenceBias) || 'neutral',
    defaultMode: (s.defaultMode as TravelMode) || 'car',
    safetyPreference: (s.safetyPreference as RouteStyle['safetyPreference']) || 'off',
    dwellMultiplier: s.dwellMultiplier,
    maxStops: s.maxStops,
    timeOfDay: (s.timeOfDay as RouteStyle['timeOfDay']) || 'any',
  };
}

function mapRouteTheme(t: PbRouteTheme): RouteTheme {
  return {
    id: t.id,
    key: t.key,
    label: t.label,
    description: t.description,
    categoryKeys: t.categoryKeys,
    avoidCategoryKeys: t.avoidCategoryKeys,
    boostCategoryKeys: t.boostCategoryKeys,
    avoidDistanceM: t.avoidDistanceM ?? null,
    avoidDistanceByCategory: {},
    stopDwellMinutes: {},
    /*
     * What the emphasis says, as the planner form's own shapes.
     *
     * These were hardcoded empty, so an emphasis' answers never reached the
     * screen and every field read "Auto" while the server applied them
     * anyway. `shortest` travels beside the weights because it is a switch,
     * and is folded back in here where the form expects one object.
     */
    routingOptions: {
      ...t.routingOptions,
      ...(t.shortest !== undefined ? { shortest: t.shortest } : {}),
    } as RouteTheme['routingOptions'],
    routeCharacteristics: { ...t.routeCharacteristics } as RouteTheme['routeCharacteristics'],
    pathOptions: { ...t.pathOptions } as RouteTheme['pathOptions'],
    minShare: t.minShare,
    isActive: t.isActive,
    sortOrder: t.sortOrder,
  };
}

function mapCityCategory(c: PbCityCategory): Category {
  return {
    id: c.id,
    key: c.key,
    label: c.label,
    group: c.categoryGroup,
    icon: c.icon || null,
    defaultDwellMinutes: c.defaultDwellMinutes,
    isSafetyInfrastructure: c.isSafetyInfrastructure,
    isAvoidable: c.isAvoidable,
    isVisitable: c.isVisitable,
    poiCount: Number(c.poiCount),
  };
}

function mapCategoryGroup(g: PbCategoryGroup): CategoryGroup {
  return {
    key: g.key,
    label: g.label,
    isInfrastructure: g.isInfrastructure,
    sortOrder: g.sortOrder,
  };
}

// ── admin: Connect → legacy type mappers ────────────────────────────

/** proto Timestamp → ISO string, or null when absent. */
function tsToIsoOrNull(ts?: Timestamp): string | null {
  return ts ? timestampDate(ts).toISOString() : null;
}

function mapManualPoi(p: PbManualPoi): ManualPoi {
  return {
    id: p.id,
    cityId: p.cityId,
    categoryId: p.categoryId,
    categoryKey: p.categoryKey,
    categoryLabel: p.categoryLabel,
    name: p.name,
    lat: p.lat,
    lng: p.lng,
    address: p.address || null,
    description: p.description || null,
    website: p.website || null,
    phone: p.phone || null,
    rating: p.rating ?? null,
    dwellMinutesOverride: p.dwellMinutesOverride ?? null,
    isActive: p.isActive,
    createdAt: p.createdAt ? timestampDate(p.createdAt).toISOString() : '',
    source: (p.source as ManualPoi['source']) || 'manual',
    closedOn: p.closedOn ?? null,
    prominence: p.prominence,
  };
}

function mapJob(j: PbJob): Job {
  return {
    id: j.id,
    cityId: j.cityId || null,
    type: j.type as Job['type'],
    status: j.status as Job['status'],
    progress: j.progress,
    message: j.message || null,
    log: j.log,
    // params/result are carried as JSON strings; the console reads them parsed.
    params: j.params ? (JSON.parse(j.params) as Record<string, unknown>) : {},
    result: j.result ? (JSON.parse(j.result) as unknown) : null,
    createdAt: j.createdAt ? timestampDate(j.createdAt).toISOString() : '',
    startedAt: tsToIsoOrNull(j.startedAt),
    finishedAt: tsToIsoOrNull(j.finishedAt),
  };
}

/** Unwraps an Enqueue/GetJob response's job, mapping it to the legacy shape. */
function enqueuedJob(res: { job?: PbJob }): Job {
  if (!res.job) throw new ApiError(502, 'NO_JOB', 'The server returned no job.');
  return mapJob(res.job);
}

/**
 * Rebuilds a whole Category from its id after a taxonomy write.
 *
 * The AdminTaxonomy Create/Update RPCs return only the affected row's id, but
 * the categories panel merges the returned Category into its list — the same
 * list it loaded from ListCategories — so re-reading that list and finding the
 * row keeps the two consistent without a dedicated get-category endpoint.
 */
async function reloadCategory(id: string): Promise<Category> {
  const res = await rpc(listCategories, {});
  const found = res.categories.find((c) => c.id === id);
  if (!found) throw new ApiError(502, 'NO_CATEGORY', 'The category could not be reloaded.');
  return mapCategory(found);
}

// ── planning: legacy PlanRequest → proto init ───────────────────────
//
// The planner still speaks the legacy PlanRequest/RoutePlan shapes; these
// translate between those and the Connect messages. Coordinate arrays are the
// only real trap: the legacy geometries are GeoJSON [lng, lat] pairs, while the
// proto carries named LatLng, so every crossing swaps ends deliberately.

function placeRefInit(ref: PlaceRef): MessageInitShape<typeof PlaceRefSchema> {
  if (ref.kind === 'poi') return { kind: 'poi', poiId: ref.poiId };
  return { kind: 'coords', lat: ref.lat, lng: ref.lng, label: ref.label ?? '' };
}

function coordStopInit(c: CoordStop): MessageInitShape<typeof CoordStopSchema> {
  return { lat: c.lat, lng: c.lng, label: c.label ?? '', dwellMinutes: c.dwellMinutes ?? 0 };
}

function breakInit(b: BreakRequest): MessageInitShape<typeof BreakRequestSchema> {
  return {
    poiId: b.poiId ?? '',
    targetMinutesFromStart: b.targetMinutesFromStart,
    anytime: b.anytime ?? false,
    durationMinutes: b.durationMinutes,
    flexibilityMinutes: b.flexibilityMinutes,
  };
}

function toPbPlanRequest(req: PlanRequest): MessageInitShape<typeof PlanRequestSchema> {
  const out: MessageInitShape<typeof PlanRequestSchema> = {
    cityId: req.cityId,
    start: placeRefInit(req.start),
    end: placeRefInit(req.end),
    mode: req.mode,
    routeStyleKeys: req.routeStyleKeys ?? [],
    mustVisitPoiIds: req.mustVisitPoiIds ?? [],
    interestCategoryKeys: req.interestCategoryKeys ?? [],
    boostCategoryKeys: req.boostCategoryKeys ?? [],
    excludePoiIds: req.excludePoiIds ?? [],
    dwellMinutesByCategoryKey: req.dwellMinutesByCategoryKey ?? {},
    safetyPreference: req.safetyPreference ?? 'off',
    avoid: {
      poiIds: req.avoid?.poiIds ?? [],
      categoryKeys: req.avoid?.categoryKeys ?? [],
      points: (req.avoid?.points ?? []).map(coordStopInit),
      ...(req.avoid?.distanceM !== undefined ? { distanceM: req.avoid.distanceM } : {}),
      distanceByPoiId: req.avoid?.distanceByPoiId ?? {},
      distanceByCategoryKey: req.avoid?.distanceByCategoryKey ?? {},
    },
    mustVisitPoints: (req.mustVisitPoints ?? []).map(coordStopInit),
    stopOrder: req.stopOrder ?? [],
    viaPoints: (req.viaPoints ?? []).map((v) => ({
      lat: v.lat, lng: v.lng, afterStopIndex: v.afterStopIndex,
    })),
    // Each legacy line is [lng, lat] pairs; the proto polyline is named LatLng.
    differentFrom: (req.differentFrom ?? []).map((line) => ({
      points: line.map(([lng, lat]) => ({ lat, lng })),
    })),
    stopDwellMinutes: req.stopDwellMinutes ?? {},
    legModes: req.legModes ?? {},
  };
  if (req.startTime !== undefined) out.startTime = req.startTime;
  if (req.date !== undefined) out.date = req.date;
  if (req.availableMinutes !== undefined) out.availableMinutes = req.availableMinutes;
  if (req.routeStyleKey !== undefined) out.routeStyleKey = req.routeStyleKey;
  if (req.themeKey !== undefined) out.themeKey = req.themeKey;
  if (req.lunch) out.lunch = breakInit(req.lunch);
  if (req.coffee) out.coffee = breakInit(req.coffee);
  if (req.avoidTolls !== undefined) out.avoidTolls = req.avoidTolls;
  if (req.maxStops !== undefined) out.maxStops = req.maxStops;
  if (req.stopOrderLocked !== undefined) out.stopOrderLocked = req.stopOrderLocked;
  if (req.routingOptions) {
    const r = req.routingOptions;
    out.routingOptions = {
      ...(r.use_highways !== undefined ? { useHighways: r.use_highways } : {}),
      ...(r.use_tolls !== undefined ? { useTolls: r.use_tolls } : {}),
      ...(r.use_ferry !== undefined ? { useFerry: r.use_ferry } : {}),
      ...(r.use_living_streets !== undefined ? { useLivingStreets: r.use_living_streets } : {}),
      ...(r.shortest !== undefined ? { shortest: r.shortest } : {}),
      ...(r.use_main_roads !== undefined ? { useMainRoads: r.use_main_roads } : {}),
      ...(r.use_residential !== undefined ? { useResidential: r.use_residential } : {}),
    };
  }
  if (req.routeCharacteristics) out.routeCharacteristics = { ...req.routeCharacteristics };
  if (req.pathOptions) out.pathOptions = { ...req.pathOptions };
  return out;
}

// ── planning: proto RoutePlan → legacy RoutePlan ────────────────────

const pairFromLatLng = (p: { lat: number; lng: number }): [number, number] => [p.lng, p.lat];

function mapRouteStop(s: PbRouteStop): PlanStop {
  return {
    kind: s.kind as StopKind,
    poiId: s.id || null,
    name: s.name,
    lat: s.lat,
    lng: s.lng,
    categoryKey: s.categoryKey ?? null,
    arrivalMinutes: s.arrivalMinutes,
    departureMinutes: s.departureMinutes,
    dwellMinutes: s.dwellMinutes,
    arrivalTime: s.arrivalTime,
    departureTime: s.departureTime,
    note: s.note ?? null,
    outsideOpeningHours: s.outsideOpeningHours ?? null,
  };
}

function mapTransitDetail(d: PbTransitLegDetail): TransitLegDetail {
  return {
    mode: d.mode,
    routeName: d.routeName ?? null,
    headsign: d.headsign ?? null,
    fromStop: d.fromStop ?? null,
    toStop: d.toStop ?? null,
    durationMinutes: d.durationMinutes,
  };
}

function mapRouteLeg(l: PbRouteLeg): PlanLeg {
  const leg: PlanLeg = {
    fromIndex: l.fromIndex,
    toIndex: l.toIndex,
    mode: l.mode as TravelMode,
    distanceMeters: l.distanceM,
    // The proto carries seconds; the legacy leg is minutes.
    durationMinutes: Math.round(l.timeS / 60),
    geometry: l.geometry.map(pairFromLatLng),
  };
  if (l.transitDetail.length) leg.transitDetail = l.transitDetail.map(mapTransitDetail);
  return leg;
}

function mapAvoidedZone(z: PbAvoidedZone): AvoidedZone {
  const zone: AvoidedZone = {
    poiId: z.poiId,
    name: z.name,
    categoryKey: z.categoryKey,
    lat: z.lat,
    lng: z.lng,
    radiusM: z.radiusM,
    polygon: z.polygon.map(pairFromLatLng),
    violated: z.violated,
  };
  if (z.fromCategoryKey !== undefined) zone.fromCategoryKey = z.fromCategoryKey;
  return zone;
}

function mapRouteAlternative(a: PbRouteAlternative): RouteAlternative {
  return {
    index: a.index,
    distanceMeters: a.distanceMeters,
    durationMinutes: a.durationMinutes,
    geometry: a.geometry.map(pairFromLatLng),
    roadAnswer: a.roadAnswer,
  };
}

function mapThemeOutcome(t: PbThemeOutcome): ThemeOutcome {
  return {
    key: t.key,
    label: t.label,
    minShare: t.minShare,
    nonAnchorStops: t.nonAnchorStops,
    onThemeStops: t.onThemeStops,
    share: t.share,
    met: t.met,
  };
}

function mapIps(a: PbIpsAssessment): IpsAssessment {
  return {
    score: a.score,
    label: a.label,
    disclaimer: a.disclaimer,
    waypointCount: a.waypointCount,
    breakdown: a.breakdown.map((b) => ({
      categoryKey: b.categoryKey,
      label: b.label,
      weight: b.weight,
      effectiveWeight: b.effectiveWeight,
      radiusM: b.radiusM,
      facilitiesWithinRadius: b.facilitiesWithinRadius,
      worstWaypointDistanceM: b.worstWaypointDistanceM ?? null,
      categoryScore: b.categoryScore,
    })),
  };
}

function mapStopRecommendation(s: PbStopRecommendation): StopRecommendation {
  return {
    poiId: s.poiId,
    name: s.name,
    categoryKey: s.categoryKey,
    categoryLabel: s.categoryLabel,
    lat: s.lat,
    lng: s.lng,
    address: s.address ?? null,
    dwellMinutes: s.dwellMinutes,
    score: s.score,
    rating: s.rating ?? null,
    userRatingCount: s.userRatingCount ?? null,
    verified: s.verified,
    detourMinutes: s.detourMinutes ?? null,
    breakRole: (s.breakRole as 'lunch' | 'coffee' | undefined) ?? null,
  };
}

function mapRoutePlan(p: PbRoutePlan): RoutePlan {
  return {
    cityId: p.cityId,
    mode: p.mode as TravelMode,
    styleKey: p.styleKey ?? null,
    styleKeys: p.styleKeys,
    theme: p.theme ? mapThemeOutcome(p.theme) : null,
    generatedAt: p.generatedAt ? timestampDate(p.generatedAt).toISOString() : '',
    startTime: p.startTime,
    stops: p.stops.map(mapRouteStop),
    legs: p.legs.map(mapRouteLeg),
    totals: p.totals
      ? {
          travelMinutes: p.totals.travelMinutes,
          dwellMinutes: p.totals.dwellMinutes,
          totalMinutes: p.totals.totalMinutes,
          distanceMeters: p.totals.distanceMeters,
        }
      : { travelMinutes: 0, dwellMinutes: 0, totalMinutes: 0, distanceMeters: 0 },
    infrastructureProximity: p.infrastructureProximity ? mapIps(p.infrastructureProximity) : null,
    avoidedZones: p.avoidedZones.map(mapAvoidedZone),
    dropped: p.dropped.map((d) => ({ poiId: d.poiId, name: d.name, reason: d.reason })),
    suggestion: p.suggestion
      ? { availableMinutes: p.suggestion.availableMinutes, recovers: p.suggestion.recovers }
      : null,
    alternatives: p.alternatives.map(mapRouteAlternative),
    roadAnswer: p.roadAnswer,
    warnings: p.warnings,
    roadNotes: p.roadNotes ?? [],
  };
}

// ── saved routes & trips: proto (JSON-string payloads) → legacy types ─
//
// The saved-route and trip stores keep their large request/plan/notes
// documents as verbatim jsonb, carried over the wire as JSON strings. These
// mappers parse those strings back into the legacy shapes the components
// consume, and rebuild the derived summary fields the list projections drop.

/** ISO string from a proto Timestamp, or '' when absent. */
function tsToIso(ts?: Timestamp): string {
  return ts ? timestampDate(ts).toISOString() : '';
}

function mapSavedRouteSummary(s: PbSavedRouteSummary): SavedRouteSummary {
  const updatedAt = tsToIso(s.updatedAt);
  return {
    id: s.id,
    cityId: s.cityId,
    // Read from the row rather than stood in for. These were '' / walk / 0 /
    // 0 because the list projection carried none of them, and the page showed
    // those placeholders as though they described the route — every saved
    // route called itself a walk of no stops lasting no time.
    cityName: s.cityName,
    name: s.name,
    mode: s.mode || 'walk',
    stopCount: s.stopCount,
    totalMinutes: s.totalMinutes,
    savedBy: s.savedBy,
    createdAt: updatedAt,
    updatedAt,
  };
}

function mapSavedRoute(r: PbSavedRoute): SavedRoute {
  const request = JSON.parse(r.requestJson) as PlanRequest;
  const plan = JSON.parse(r.planJson) as RoutePlan;
  const notes = r.notesJson ? (JSON.parse(r.notesJson) as Record<string, string>) : {};
  return {
    id: r.id,
    cityId: r.cityId,
    cityName: '',
    name: r.name,
    // Derived from the plan exactly as the legacy summary did: the visit
    // stops are the stops minus the fixed start and end.
    mode: plan?.mode ?? 'walk',
    stopCount: Math.max(0, (plan?.stops?.length ?? 2) - 2),
    // The single-route fetch does not select it; only the list projection
    // does, and only the list shows it.
    savedBy: '',
    totalMinutes: plan?.totals?.totalMinutes ?? 0,
    createdAt: tsToIso(r.createdAt),
    updatedAt: tsToIso(r.updatedAt),
    request,
    plan,
    notes,
  };
}

function mapSavedTripSummary(s: PbTripSummary): SavedTripSummary {
  return {
    id: s.id,
    cityId: s.cityId,
    cityName: s.cityName,
    name: s.name,
    dayCount: s.dayCount,
    plannedDays: s.plannedDays,
    stopCount: s.stopCount,
    savedBy: s.savedBy,
    createdAt: tsToIso(s.createdAt),
    updatedAt: tsToIso(s.updatedAt),
  };
}

function mapSavedTrip(t: PbTrip): SavedTrip {
  if (!t.summary) throw new ApiError(502, 'NO_TRIP', 'The trip is missing its summary.');
  return {
    ...mapSavedTripSummary(t.summary),
    request: JSON.parse(t.requestJson) as TripRequest,
    plan: JSON.parse(t.planJson) as TripPlan,
  };
}

/**
 * One plan over Connect, abandonable by the given signal. Bypasses the `rpc`
 * helper only because that helper does not thread a signal through.
 */
async function callPlan(request: PlanRequest, signal?: AbortSignal): Promise<RoutePlan> {
  const res = await transport.unary(
    planMethod, signal, undefined, undefined, toPbPlanRequest(request),
  );
  if (!res.message.plan) throw new ApiError(502, 'NO_PLAN', 'The planner returned no route.');
  return mapRoutePlan(res.message.plan);
}

/** Where the REST calls go. See ./origin for how this is chosen. */
const BASE_URL = apiBaseUrl;

/**
 * Identifies this browser for saved routes.
 *
 * The MVP has no authentication, so this is a random opaque id kept in
 * localStorage — not a security boundary. Clearing site data loses the list
 * of saved routes.
 */
const CLIENT_KEY_STORAGE = 'travplaces.clientKey';

export function clientKey(): string {
  let key = localStorage.getItem(CLIENT_KEY_STORAGE);
  if (!key) {
    key = crypto.randomUUID();
    localStorage.setItem(CLIENT_KEY_STORAGE, key);
  }
  return key;
}

/** Carries the API's machine-readable code so the UI can react to specifics. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`${BASE_URL}${path}`, {
      ...init,
      headers: {
        'x-travplaces-client': clientKey(),
        ...(init.body && !(init.body instanceof Blob)
          ? { 'content-type': 'application/json' }
          : {}),
        ...init.headers,
      },
    });
  } catch (err) {
    // An abandoned request is not a failure. Rethrown as itself so callers
    // can tell "nobody wants this any more" from "the server is down".
    if (err instanceof DOMException && err.name === 'AbortError') throw err;
    throw new ApiError(0, 'NETWORK', 'Could not reach the TravPlaces API. Is the server running?');
  }

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  let parsed: unknown = null;
  try {
    parsed = text ? JSON.parse(text) : null;
  } catch {
    // fall through with the raw text below
  }

  if (!response.ok) {
    const body = parsed as ApiErrorBody | null;
    throw new ApiError(
      response.status,
      body?.error?.code ?? 'UNKNOWN',
      body?.error?.message ?? text.slice(0, 200) ?? response.statusText,
      body?.error?.details,
    );
  }

  return parsed as T;
}

/**
 * One file to a plain route, as the whole request body, with progress.
 *
 * XMLHttpRequest rather than fetch, which is otherwise what everything here
 * uses. An .osm.pbf is tens to hundreds of megabytes and fetch reports
 * nothing at all until the last byte has gone — the operator would watch a
 * still screen for minutes with no way to tell a slow upload from a dead one.
 * `upload.onprogress` is the only thing a browser offers that says how far it
 * has got.
 *
 * The token is attached by hand for the same reason: this does not go through
 * the Connect transport, which is where the Authorization header is normally
 * added (see ./transport).
 */
function upload<T>(
  path: string,
  file: File,
  onProgress?: (fraction: number) => void,
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${BASE_URL}${path}`);

    const token = accessToken();
    if (token) xhr.setRequestHeader('authorization', `Bearer ${token}`);
    // The body is the file itself, not a form: the server streams it straight
    // to disk, and a multipart wrapper would only be something to unwrap.
    xhr.setRequestHeader('content-type', 'application/octet-stream');

    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) onProgress?.(event.loaded / event.total);
    };

    xhr.onerror = () => reject(
      new ApiError(0, 'NETWORK', 'Could not reach the TravPlaces API. Is the server running?'),
    );
    xhr.onabort = () => reject(new ApiError(0, 'ABORTED', 'The upload was cancelled.'));

    xhr.onload = () => {
      let parsed: unknown = null;
      try {
        parsed = xhr.responseText ? JSON.parse(xhr.responseText) : null;
      } catch {
        // fall through with the raw text below
      }

      if (xhr.status < 200 || xhr.status >= 300) {
        const body = parsed as ApiErrorBody | null;
        reject(new ApiError(
          xhr.status,
          body?.error?.code ?? 'UNKNOWN',
          body?.error?.message ?? xhr.responseText.slice(0, 200) ?? xhr.statusText,
          body?.error?.details,
        ));

        return;
      }

      resolve(parsed as T);
    };

    xhr.send(file);
  });
}

const get = <T>(path: string) => request<T>(path);

// ── public ──────────────────────────────────────────────────────────

/**
 * Absolute URL of a city's PMTiles archive. Absolute because the pmtiles
 * client fetches it directly, outside the app's own request helper.
 */
/**
 * Turns an API-relative path into one the browser can load directly.
 *
 * Used for anything fetched by the platform rather than by this module — an
 * <img> src, say — which does not go through `request` and so does not pick
 * up the base URL on its own.
 */
export function absoluteUrl(path: string): string {
  if (path.startsWith('http')) return path;
  // Genuinely absolute, even when the base is relative: this is for things
  // the platform fetches rather than this module — an <img> src, or the
  // pmtiles reader, which resolve against their own idea of the document.
  return new URL(`${BASE_URL}${path}`, window.location.origin).toString();
}

/**
 * Where the map's tiles come from for this city.
 *
 * One archive covers every city when the pipeline has built one, and that is
 * the point: the URL is then the same whichever city is chosen, so switching
 * city changes no source, tears down no layer, and moves only the camera.
 * `shared` comes from the settings the pages already load; without it each
 * city falls back to its own archive and the old swap still happens.
 */
/**
 * The ground a plan would search, named by its ends rather than its width.
 *
 * The server derives the radius from the mode and the time available, which
 * is tuned per mode in settings — so a caller says where it is going, not how
 * wide to look.
 */
export interface CorridorQuery {
  startLat: number;
  startLng: number;
  endLat: number;
  endLng: number;
  mode?: string;
  availableMinutes?: number;
}

let sharedBasemap = false;

export function basemapUrlFor(
  city: { slug: string; hasBasemap: boolean },
  shared: boolean = sharedBasemap,
): string | null {
  if (shared) return absoluteUrl('/api/basemap/all');
  return city.hasBasemap ? absoluteUrl(`/api/basemap/${city.slug}`) : null;
}

/**
 * The plan currently on the wire, if any.
 *
 * Module-level on purpose: "only the latest" has to hold across the whole
 * screen, not per component. The planner form, the emphases beside the map
 * and anything else that plans are all asking the same expensive question.
 */
let planInFlight: AbortController | null = null;

/*
 * One in-flight call per channel, for the endpoints worth abandoning.
 *
 * `planLatest` had this for plans alone, and plans are not the only thing the
 * server works hard at: recommendations run the candidate search and the
 * provider enrichment behind it, with a sixty-place budget. Pressing "Suggest
 * somewhere" twice left both running, and the first answer arrived to be
 * discarded having held the pipeline for the whole time.
 *
 * Keyed by name rather than one shared controller, so asking for stops does
 * not abandon a lunch list somebody is still waiting on.
 */
const inFlight = new Map<string, AbortController>();

function latest<T>(channel: string, run: (signal: AbortSignal) => Promise<T>): Promise<T> {
  inFlight.get(channel)?.abort();

  const controller = new AbortController();
  inFlight.set(channel, controller);

  return run(controller.signal).finally(() => {
    if (inFlight.get(channel) === controller) inFlight.delete(channel);
  });
}

/** Abandons whatever plan is running. Safe to call when none is. */
export function cancelPlan(): void {
  planInFlight?.abort();
  planInFlight = null;
}

export const api = {
  health: () => get<HealthReport>('/api/health'),

  cities: async (_includeUnready = false) => {
    const res = await rpc(listCities, {});
    return res.cities.map(mapCity);
  },
  city: async (id: string) => {
    const res = await rpc(getCity, { id });
    if (!res.city) throw new ApiError(404, 'NOT_FOUND', 'City not found');
    return mapCity(res.city);
  },

  categories: async () => {
    const res = await rpc(listCategories, {});
    return res.categories.map(mapCategory);
  },

  /** The groups categories are filed under, named and ordered by Admin. */
  categoryGroups: async () => {
    const res = await rpc(listCategoryGroups, {});
    return res.categoryGroups.map(mapCategoryGroup);
  },

  /** One place with whatever external detail is cached for it. */
  placeDetails: async (id: string): Promise<PlaceDetails> => {
    const res = await rpc(getPoiDetails, { id });
    if (!res.poi) throw new ApiError(404, 'NOT_FOUND', 'Place not found');
    const poi = mapPoi(res.poi);
    const ext = res.external;
    return {
      poi: {
        ...poi,
        phone: res.phone || poi.phone,
        website: res.website || poi.website,
      },
      external: ext
        ? {
          externalId: ext.externalId || null,
          rating: ext.rating ?? null,
          userRatingCount: ext.userRatingCount ?? null,
          priceLevel: ext.priceLevel ?? null,
          primaryType: ext.primaryType || null,
          businessStatus: ext.businessStatus || null,
          // Shape varies by provider, so it is parsed defensively and
          // treated as absent when it is not usable JSON.
          openingHours: safeJson(ext.openingHoursJson),
          matchConfidence: ext.matchConfidence ?? null,
          expiresAt: ext.expiresAt,
        }
        : null,
      lookupOutcome: (res.lookupOutcome || 'never') as PlaceDetails['lookupOutcome'],
      /*
       * URLs this API serves, never the provider's own references — the
       * reference is a billable handle the browser has no use for, and the
       * redirect endpoint behind these is the only thing that should hold one.
       *
       * Attribution rides along with each picture because the terms require it
       * to be shown wherever the photo is, and a client that has to look it up
       * elsewhere is a client that will one day forget.
       */
      photos: res.photos.map((photo) => ({
        url: photo.url,
        widthPx: photo.widthPx ?? null,
        heightPx: photo.heightPx ?? null,
        attributions: photo.attributions.map((a) => ({
          displayName: a.displayName,
          uri: a.uri ?? null,
        })),
      })),
      // Sent by the server, so the client cannot forget it — and keyed to a
      // match worth trusting, since a rejected one puts nothing on screen.
      attribution: res.attribution || null,
    };
  },
  settings: async () => {
    const res = await rpc(listSettings, {});
    const all: Record<string, unknown> = {};
    for (const s of res.settings) {
      try {
        all[s.key] = s.valueJson ? JSON.parse(s.valueJson) : null;
      } catch {
        // A value that is not valid JSON is passed through as its raw string.
        all[s.key] = s.valueJson;
      }
    }
    // Remembered here so `basemapUrlFor` needs no argument: every page loads
    // the settings before it draws a map, and none of them should have to
    // thread this through to say where the tiles are.
    sharedBasemap = all['map.shared_basemap'] === true;
    return all;
  },
  /**
   * Categories with how many places each holds.
   *
   * Given a corridor, it counts what is IN the corridor rather than what the
   * city holds somewhere — and not by city, since a route crosses freely.
   * The width is derived server-side from the mode and time, so this never
   * carries a second copy of that formula.
   */
  cityCategories: async (cityId: string, _corridor?: CorridorQuery, _scope?: 'avoid') => {
    const res = await rpc(listCityCategories, { cityId });
    return res.categories.map(mapCityCategory);
  },

  routeStyles: async (cityId?: string) => {
    const res = await rpc(listRouteStyles, { cityId: cityId ?? '' });
    return res.routeStyles.map(mapRouteStyle);
  },

  searchPois: async (params: {
    /** Omitted searches every city — what the endpoint pickers do. */
    cityId?: string;
    q?: string;
    categoryKeys?: string[];
    visitableOnly?: boolean;
    lat?: number;
    lng?: number;
    limit?: number;
    /** Rows to skip, for a picker fetching the next page as it scrolls. */
    offset?: number;
    /**
     * Also count every match, for a caller that shows how much there is.
     *
     * Asked for rather than always returned: counting them all means the
     * database cannot stop at the page, which is a cost no picker showing
     * ten rows should pay.
     */
    withTotal?: boolean;
  }) => {
    const res = await rpc(searchPoisMethod, {
      cityId: params.cityId ?? '',
      q: params.q ?? '',
      categoryKeys: params.categoryKeys ?? [],
      visitableOnly: params.visitableOnly ?? false,
      limit: params.limit ?? 0,
      offset: params.offset ?? 0,
      withTotal: params.withTotal ?? false,
    });

    /*
     * The rows AND whether there are more of them.
     *
     * Returned as an object rather than a bare array, because a caller that
     * pages has to know when to stop asking and cannot tell from the rows:
     * a search matching exactly one page would look like there was another.
     */
    return { pois: res.pois.map(mapPoi), hasMore: res.hasMore, total: res.total };
  },

  poi: async (id: string) => {
    const res = await rpc(getPoi, { id });
    if (!res.poi) throw new ApiError(404, 'NOT_FOUND', 'Place not found');
    return mapPoi(res.poi);
  },

  /** External place search, when a provider key is configured. */
  searchExternal: async (cityId: string, q: string) => {
    const res = await rpc(searchExternalMethod, { cityId, q });
    return {
      available: res.available,
      results: JSON.parse(res.resultsJson) as import('./types').ExternalPlace[],
    };
  },

  /** Names whatever is at a coordinate, so a dropped pin is not just numbers. */
  describePlace: async (cityId: string, lat: number, lng: number) => {
    const res = await rpc(describePlaceMethod, { cityId, lat, lng });
    return JSON.parse(res.description) as import('./types').PlaceDescription;
  },

  /**
   * Every city's region as GeoJSON — the real boundary where one is stored,
   * the bounding box otherwise. For seeing which ground a city actually
   * covers, which stopped being obvious once a city could be a whole state.
   */
  /*
   * Narrowed to the cities the described route crosses, when one is given.
   * Drawing every outline answers "what exists"; these answer "whose data is
   * this plan made of", which is the question a cross-country route raises.
   */
  cityRegions: async (_corridor?: CorridorQuery) => {
    const res = await rpc(getCityRegions, {});
    // The RPC hands back the FeatureCollection as a JSON string; the map wants
    // the parsed object, exactly as the REST endpoint used to return.
    return JSON.parse(res.geojson) as unknown;
  },

  /** The cities a route crosses — the ground the category counts cover. */
  citiesOnRoute: async (corridor: CorridorQuery) => {
    const res = await rpc(citiesOnRouteMethod, {
      startLat: corridor.startLat,
      startLng: corridor.startLng,
      endLat: corridor.endLat,
      endLng: corridor.endLng,
    });
    return {
      cities: res.cities.map((c) => ({ id: c.id, slug: c.slug, name: c.name })),
    };
  },

  /** POIs as GeoJSON for the map's category browser. */
  poisGeoJson: async (params: {
    cityId: string; categoryKeys?: string[]; limit?: number;
    /**
     * The ground to fetch, [west, south, east, north].
     *
     * Omitted asks for the whole city, which is nearly two megabytes where
     * every kind is ticked — and the limit that holds that down cuts the
     * list by name, so the map drew everything from A to M and nothing
     * after. A browsing layer sends what it is showing; the avoidance
     * preview sends nothing, because it is a claim about the whole route
     * rather than about the view.
     */
    bbox?: ViewBox;
    /**
     * `avoid` asks the question avoidance asks: the roads and their reach,
     * not whole cities the route passes through. Browsing covers the city;
     * avoidance cannot, and a preview drawn the browsing way would promise
     * more than the plan delivers.
     */
    for?: 'avoid';
  } & Partial<CorridorQuery>) => {
    const res = await rpc(getPoiGeojson, {
      cityId: params.cityId,
      categoryKeys: params.categoryKeys ?? [],
      limit: params.limit ?? 0,
      bbox: params.bbox ?? [],
    });
    // The RPC returns the FeatureCollection as a JSON string; callers expect
    // the parsed object, as the REST endpoint used to return.
    return JSON.parse(res.geojson) as unknown;
  },

  /**
   * A plan, abandonable.
   *
   * The signal matters more than it looks: a route avoiding a whole category
   * takes tens of seconds, and clearing the form or straightening a path
   * during one used to leave the browser holding a connection open for an
   * answer nobody would read — with the replacement plan queued behind it.
   */
  plan: (request: PlanRequest, signal?: AbortSignal) => callPlan(request, signal),

  /**
   * A plan, and only ever one.
   *
   * Planning is the expensive thing this app does — a matrix, a solve, real
   * routing, and the provider lookups behind them — and every caller that
   * starts one has, by starting it, stopped wanting the answer to the last.
   * A form edited three times, or three emphases built in a row, used to
   * leave three pipelines running: the first two answers arrive to be
   * discarded, having each held a Valhalla worker for tens of seconds.
   *
   * So the rule lives here, where the endpoint does, rather than in each
   * caller: asking for a plan abandons the plan before it.
   */
  planLatest: (request: PlanRequest) => {
    cancelPlan();
    const controller = new AbortController();
    planInFlight = controller;
    return callPlan(request, controller.signal)
      .finally(() => {
        if (planInFlight === controller) planInFlight = null;
      });
  },

  /** The themes on offer, for the planner's alternatives switcher. */
  themes: async () => {
    const res = await rpc(listRouteThemes, {});
    return res.themes.map(mapRouteTheme);
  },

  /**
   * The same journey planned once per theme.
   *
   * One request rather than three, so the three days arrive together and can
   * be compared rather than watched to load one at a time.
   */
  planThemes: async (request: PlanRequest) => {
    const res = await rpc(planThemesMethod, toPbPlanRequest(request));
    return {
      alternatives: res.alternatives.map((a) => ({
        theme: a.theme,
        label: a.label,
        plan: a.plan ? mapRoutePlan(a.plan) : null,
        error: a.error ?? null,
      })),
    };
  },

  /**
   * What the corridor offers, ranked, without planning anything.
   *
   * Takes the same request a plan would, so the suggestions follow whatever
   * is already set — mode, time, interests, avoidance — rather than being a
   * generic list of the city's highlights.
   */
  recommendations: async (
    request: PlanRequest,
    /**
     * Which lists to build. The REST endpoint filtered server-side by this;
     * the RPC returns all three, so it is now accepted for signature
     * compatibility and left to the caller to read only what it asked for.
     */
    _lists?: readonly ('stops' | 'lunch' | 'coffee')[],
  ) => {
    const res = await latest(
      // One channel per list, so the three panels do not cancel each other.
      `recommendations:${[..._lists ?? ['stops']].sort().join(',')}`,
      (signal) => rpc(planRecommendationsMethod, toPbPlanRequest(request), signal),
    );
    return {
      stops: res.stops.map(mapStopRecommendation),
      lunch: res.lunch.map(mapStopRecommendation),
      coffee: res.coffee.map(mapStopRecommendation),
    };
  },

  /**
   * Several days, planned in order.
   *
   * Each day is one ordinary plan; what this adds is what lives between them
   * — where each starts, its weekday for opening hours, and the rule that a
   * place seen on Monday is not offered again on Thursday.
   */
  /**
   * The ground a plan would search, before planning anything.
   *
   * The buffered corridor itself, straight from PostGIS, so what is drawn is
   * what will be looked in. Costs a geometry call and nothing else.
   */
  /**
   * Turn-by-turn for a plan that is already made, for export.
   *
   * Takes the request the plan came from — so the costing, the road answers
   * and the toll preference are the ones that drew the line — together with
   * the stop order the optimiser settled on and the zones the plan reported
   * avoiding. Nothing is worked out again: re-running the optimiser could
   * answer differently, and directions that disagree with the itinerary
   * printed beside them are worse than none.
   */
  directions: async (
    request: PlanRequest,
    points: { lat: number; lng: number }[],
    exclude: [number, number][][] = [],
  ): Promise<DirectionsLeg[]> => {
    const res = await rpc(planDirectionsMethod, {
      plan: toPbPlanRequest(request),
      points: points.map((p) => ({ lat: p.lat, lng: p.lng })),
      // The plan reports its zones as [lng, lat], which is what the map wants;
      // the wire wants them named.
      exclude: exclude.map((ring) => ({
        ring: ring.map(([lng, lat]) => ({ lat, lng })),
      })),
    });

    return res.legs.map((leg) => ({
      fromIndex: leg.fromIndex,
      toIndex: leg.toIndex,
      distanceMeters: leg.distanceM,
      durationSeconds: leg.timeS,
      maneuvers: leg.maneuvers.map((m) => ({
        instruction: m.instruction,
        streetNames: m.streetNames,
        distanceMeters: m.distanceM,
        durationSeconds: m.timeS,
        type: m.type,
        lat: m.lat,
        lng: m.lng,
      })),
    }));
  },

  /**
   * How often each place in a city has been planned.
   *
   * One call for a whole catalogue, where `poiCoverage` is one call for a
   * round's own stops: a browser marking up a list of thousands wants a
   * figure beside each name, not every covering of each.
   *
   * Returned keyed by place id because every caller looks places up rather
   * than walking the list. A place that is missing has never been planned,
   * which is most of them.
   */
  placeTallies: async (cityId: string): Promise<Map<string, PlaceTally>> => {
    const res = await rpc(placeTalliesMethod, { cityId });

    return new Map(res.tallies.map((t) => [t.poiId, {
      poiId: t.poiId,
      plans: t.plans,
      lastPlanDate: t.lastPlanDate,
      name: t.name,
      lat: t.lat,
      lng: t.lng,
      categoryLabel: t.categoryLabel,
    }]));
  },

  /**
   * What of this route has been covered before, and by whom.
   *
   * Asked with the route's own places rather than the whole city, so the
   * answer is about what is on screen. A place with no history is left out of
   * the reply rather than returned empty, so the caller looks its stops up in
   * the result instead of walking it.
   */
  poiCoverage: async (
    cityId: string,
    poiIds: string[],
    points: CoveragePoint[] = [],
  ): Promise<PoiCoverage[]> => {
    if (poiIds.length === 0 && points.length === 0) return [];
    const res = await rpc(poiCoverageMethod, { cityId, poiIds, points });

    return res.entries.map((entry) => ({
      poiId: entry.poiId,
      key: entry.key || entry.poiId,
      totalMinutes: entry.totalMinutes,
      coverings: entry.coverings.map((c) => ({
        id: c.id,
        name: c.name,
        source: c.source === 'operation' ? ('operation' as const) : ('route' as const),
        by: c.by,
        byName: c.byName,
        stopIndex: c.stopIndex,
        prevStop: c.prevStop,
        nextStop: c.nextStop,
        at: tsToIsoOrNull(c.at),
        dwellMinutes: c.dwellMinutes,
        day: c.day,
        planStops: c.planStops,
        planDate: c.planDate,
        planTime: c.planTime,
        metres: c.metres,
        stopName: c.stopName,
      })),
    }));
  },

  coverage: async (request: PlanRequest) => {
    const res = await rpc(planCoverageMethod, toPbPlanRequest(request));
    return {
      radiusM: res.radiusM,
      spanM: res.spanM,
      roundTrip: res.roundTrip,
      // PostGIS geometry arrives as a GeoJSON string; the map wants it parsed,
      // as the REST endpoint used to return.
      area: res.areaGeojson ? (JSON.parse(res.areaGeojson) as unknown) : null,
    };
  },

  planTrip: async (request: TripRequest) => {
    const res = await rpc(planTripMethod, { requestJson: JSON.stringify(request) });
    return JSON.parse(res.planJson) as TripPlan;
  },

  /**
   * One day, re-planned alone. Returns which later days it made stale.
   *
   * The RPC returns only the re-planned day; the legacy `stale` list has no
   * proto field, so it comes back empty — the caller recomputes staleness
   * from the day it got.
   */
  planTripDay: async (trip: TripRequest, index: number, alreadyPlanned: string[]) => {
    const res = await rpc(planTripDayMethod, {
      requestJson: JSON.stringify(trip),
      index,
      alreadyPlanned,
    });
    return { day: JSON.parse(res.dayJson) as TripDay, stale: [] as number[] };
  },

  /** This account's saved operations, or everybody's. See `everyone`. */
  trips: async (everyone = false) => {
    const res = await rpc(listTripsMethod, { ownerKey: clientKey(), everyone });
    return res.trips.map(mapSavedTripSummary);
  },
  trip: async (id: string) => {
    const res = await rpc(getTripMethod, { id });
    if (!res.trip) throw new ApiError(404, 'NOT_FOUND', 'Trip not found');
    return mapSavedTrip(res.trip);
  },
  saveTrip: async (body: { cityId: string; name: string; request: TripRequest; plan: TripPlan }) => {
    const res = await rpc(saveTripMethod, {
      ownerKey: clientKey(),
      cityId: body.cityId,
      name: body.name,
      requestJson: JSON.stringify(body.request),
      planJson: JSON.stringify(body.plan),
    });
    if (!res.trip) throw new ApiError(502, 'NO_TRIP', 'Save returned no trip.');
    return mapSavedTrip(res.trip);
  },
  updateTrip: async (
    id: string,
    body: Partial<{ name: string; request: TripRequest; plan: TripPlan }>,
  ) => {
    const res = await rpc(updateTripMethod, {
      id,
      ownerKey: clientKey(),
      ...(body.name !== undefined ? { name: body.name } : {}),
      ...(body.request !== undefined ? { requestJson: JSON.stringify(body.request) } : {}),
      ...(body.plan !== undefined ? { planJson: JSON.stringify(body.plan) } : {}),
    });
    if (!res.trip) throw new ApiError(404, 'NOT_FOUND', 'Trip not found');
    return mapSavedTrip(res.trip);
  },
  deleteTrip: async (id: string) => {
    await rpc(deleteTripMethod, { id, ownerKey: clientKey() });
  },

  /**
   * This account's saved routes.
   *
   * `everyone` asks for every operator's instead, which is the other
   * question somebody has on that page and the same ground the coverage
   * panel already reports on.
   */
  savedRoutes: async (everyone = false) => {
    const res = await rpc(listSavedRoutesMethod, { ownerKey: clientKey(), everyone });
    return res.routes.map(mapSavedRouteSummary);
  },
  savedRoute: async (id: string) => {
    const res = await rpc(getSavedRouteMethod, { id });
    if (!res.route) throw new ApiError(404, 'NOT_FOUND', 'Saved route not found');
    return mapSavedRoute(res.route);
  },
  saveRoute: async (body: { cityId: string; name: string; request: PlanRequest; plan: RoutePlan }) => {
    const res = await rpc(saveRouteMethod, {
      ownerKey: clientKey(),
      cityId: body.cityId,
      name: body.name,
      requestJson: JSON.stringify(body.request),
      planJson: JSON.stringify(body.plan),
    });
    if (!res.route) throw new ApiError(502, 'NO_ROUTE', 'Save returned no route.');
    return mapSavedRoute(res.route);
  },
  updateSavedRoute: async (
    id: string,
    body: Partial<{ name: string; plan: RoutePlan; notes: Record<string, string> }>,
  ) => {
    // The RPC's name/plan/notes fields are plain (non-optional) strings; an
    // unset patch field is sent as an empty string, which the store reads as
    // "leave this one alone".
    const res = await rpc(updateSavedRouteMethod, {
      id,
      name: body.name ?? '',
      planJson: body.plan !== undefined ? JSON.stringify(body.plan) : '',
      notesJson: body.notes !== undefined ? JSON.stringify(body.notes) : '',
    });
    if (!res.route) throw new ApiError(404, 'NOT_FOUND', 'Saved route not found');
    return mapSavedRoute(res.route);
  },
  deleteSavedRoute: async (id: string) => {
    await rpc(deleteSavedRouteMethod, { id });
  },
};

// ── admin ───────────────────────────────────────────────────────────

export const adminApi = {
  /**
   * Finds a place's boundary, for filling in a new city. Proxied so
   * Nominatim sees one identified client rather than every open tab.
   */
  geocode: async (q: string, country: string, feature: string) => {
    const res = await rpc(geocodeMethod, { q, country, feature });
    return {
      results: JSON.parse(res.resultsJson) as import('./types').GeocodedPlace[],
    };
  },

  /*
   * ── places added by hand ──────────────────────────────────────────
   * Only ever manual rows: an imported place is rewritten by its next
   * import, so the API refuses to edit one and this cannot offer to.
   */
  /** A page of a city's places, from any source. */
  pois: async (params: {
    cityId: string; q?: string; sources?: string; categoryKeys?: string;
    includeHidden?: boolean; notableOnly?: boolean; limit?: number; offset?: number;
  }): Promise<{ places: ManualPoi[]; total: number }> => {
    const res = await rpc(listAdminPois, {
      cityId: params.cityId,
      q: params.q ?? '',
      // The REST filters were comma-joined strings; the proto wants lists.
      sources: params.sources ? params.sources.split(',') : [],
      categoryKeys: params.categoryKeys ? params.categoryKeys.split(',') : [],
      includeHidden: params.includeHidden ?? false,
      notableOnly: params.notableOnly ?? false,
      limit: params.limit ?? 0,
      offset: params.offset ?? 0,
    });
    return { places: res.places.map(mapManualPoi), total: res.total };
  },

  /** Category by source, for reviewing where a city's data came from. */
  poiCounts: async (params: {
    cityId: string; q?: string; sources?: string; includeHidden?: boolean;
  }): Promise<{
    rows: {
      categoryKey: string; label: string; group: string; isVisitable: boolean;
      osm: number; fsq: number; manual: number; total: number;
    }[];
    hidden: number;
  }> => {
    const res = await rpc(poiCountsMethod, {
      cityId: params.cityId,
      q: params.q ?? '',
      sources: params.sources ? params.sources.split(',') : [],
      includeHidden: params.includeHidden ?? false,
    });
    return {
      rows: res.rows.map((r) => ({
        categoryKey: r.categoryKey,
        label: r.label,
        group: r.group,
        isVisitable: r.isVisitable,
        osm: r.osm,
        fsq: r.fsq,
        manual: r.manual,
        total: r.total,
      })),
      hidden: res.hidden,
    };
  },

  /**
   * Searches the provider for a place to add, each result carrying whatever
   * we already hold near it. Costs a request per search, so it is driven by
   * a button rather than by typing.
   */
  searchPlacesToAdd: async (cityId: string, q: string) => {
    const res = await rpc(searchPlacesToAddMethod, { cityId, q });
    return {
      available: res.available,
      reason: res.reason || undefined,
      results: JSON.parse(res.resultsJson) as import('./types').AdminPlaceResult[],
    };
  },

  /** What a proposed place might duplicate, asked before anything is saved. */
  poiDuplicates: async (body: {
    cityId: string; name: string; lat: number; lng: number; excludeId?: string;
  }): Promise<{
    candidates: DuplicateCandidate[];
    radiusM: number;
    similarity: number;
  }> => {
    const res = await rpc(poiDuplicatesMethod, {
      cityId: body.cityId,
      name: body.name,
      lat: body.lat,
      lng: body.lng,
      ...(body.excludeId !== undefined ? { excludeId: body.excludeId } : {}),
    });
    return {
      candidates: res.candidates.map((c) => ({
        id: c.id,
        name: c.name,
        categoryKey: c.categoryKey,
        categoryLabel: c.categoryLabel,
        source: c.source as DuplicateCandidate['source'],
        address: c.address || null,
        lat: c.lat,
        lng: c.lng,
        distanceM: c.distanceM,
        nameSimilarity: c.nameSimilarity,
        isMatch: c.isMatch,
      })),
      radiusM: res.radiusM,
      similarity: res.similarity,
    };
  },

  createPoi: async (
    body: import('./types').ManualPoiInput & { force?: boolean },
  ): Promise<ManualPoi> => {
    const res = await rpc(createPoiMethod, {
      cityId: body.cityId,
      categoryId: body.categoryId,
      categoryKey: '',
      name: body.name,
      lat: body.lat,
      lng: body.lng,
      address: body.address ?? '',
      description: body.description ?? '',
      website: body.website ?? '',
      phone: body.phone ?? '',
      ...(body.rating != null ? { rating: body.rating } : {}),
      ...(body.dwellMinutesOverride != null
        ? { dwellMinutesOverride: body.dwellMinutesOverride }
        : {}),
      force: body.force ?? false,
    });
    if (!res.poi) throw new ApiError(502, 'NO_POI', 'Create returned no place.');
    return mapManualPoi(res.poi);
  },

  updatePoi: async (
    id: string,
    body: Partial<Omit<import('./types').ManualPoiInput, 'cityId'>>
      & { isActive?: boolean; force?: boolean },
  ): Promise<ManualPoi> => {
    const res = await rpc(updatePoiMethod, {
      id,
      ...(body.categoryId !== undefined ? { categoryId: body.categoryId } : {}),
      ...(body.name !== undefined ? { name: body.name } : {}),
      ...(body.lat !== undefined ? { lat: body.lat } : {}),
      ...(body.lng !== undefined ? { lng: body.lng } : {}),
      ...(body.address !== undefined ? { address: body.address ?? '' } : {}),
      ...(body.description !== undefined ? { description: body.description ?? '' } : {}),
      ...(body.website !== undefined ? { website: body.website ?? '' } : {}),
      ...(body.phone !== undefined ? { phone: body.phone ?? '' } : {}),
      ...(body.rating != null ? { rating: body.rating } : {}),
      ...(body.dwellMinutesOverride != null
        ? { dwellMinutesOverride: body.dwellMinutesOverride }
        : {}),
      ...(body.isActive !== undefined ? { isActive: body.isActive } : {}),
      force: body.force ?? false,
    });
    if (!res.poi) throw new ApiError(502, 'NO_POI', 'Update returned no place.');
    return mapManualPoi(res.poi);
  },

  deletePoi: async (id: string): Promise<{ deleted: boolean }> => {
    const res = await rpc(deletePoiMethod, { id });
    return { deleted: res.deleted };
  },

  system: async (): Promise<SystemStatus> => {
    // The Connect status is simplified to database liveness; the legacy
    // console fields (disk / queue / dataDir) have no equivalent on this
    // deployment, so they report their absence. `backend` is real, though:
    // the RPC call succeeding at all means the backend is up, and
    // databaseOk/databaseDetail reflect an actual DB ping.
    const res = await rpc(systemStatusMethod, {});
    return {
      backend: { ok: res.databaseOk, detail: res.databaseDetail },
      disk: null,
      queue: { active: null, queued: 0 },
      dataDir: '',
      // Whether the API holds a Places key. `enabled` is the operator's own
      // switch and lives in settings, so it is read there rather than here.
      externalSearch: {
        keyConfigured: res.externalSearchKeyConfigured,
        enabled: false,
      },
      unavailableJobs: Object.fromEntries(
        res.unavailableJobs.map((job) => [job.type, job.reason]),
      ),
    };
  },
  cities: async (): Promise<City[]> => {
    const res = await rpc(listAllCities, {});
    return res.cities.map(mapCity);
  },

  createCity: async (body: {
    name: string;
    slug: string;
    timezone: string;
    units: 'imperial' | 'metric';
    /** ISO 3166-1 alpha-2, upper case. */
    countryCode?: string;
    bbox: [number, number, number, number];
    defaultAvoidDistanceM?: number;
  }): Promise<City> => {
    const res = await rpc(createCityMethod, {
      name: body.name,
      slug: body.slug,
      timezone: body.timezone,
      units: body.units,
      countryCode: body.countryCode ?? '',
      bboxMinLng: body.bbox[0],
      bboxMinLat: body.bbox[1],
      bboxMaxLng: body.bbox[2],
      bboxMaxLat: body.bbox[3],
      defaultAvoidDistanceM: body.defaultAvoidDistanceM ?? 0,
    });
    if (!res.city) throw new ApiError(502, 'NO_CITY', 'Create returned no city.');
    return mapCity(res.city);
  },

  /**
   * Patches a city's mutable fields. Only the fields the proto carries reach
   * the backend — name, timezone, units, country, avoid distance and the bbox
   * (patched as a unit); other legacy keys (e.g. poiSource) have no Connect
   * field yet and are ignored.
   */
  updateCity: async (id: string, body: Record<string, unknown>): Promise<City> => {
    const bbox = body.bbox;
    const res = await rpc(updateCityMethod, {
      id,
      ...(typeof body.name === 'string' ? { name: body.name } : {}),
      ...(typeof body.timezone === 'string' ? { timezone: body.timezone } : {}),
      ...(typeof body.units === 'string' ? { units: body.units } : {}),
      ...(typeof body.countryCode === 'string' ? { countryCode: body.countryCode } : {}),
      ...(typeof body.defaultAvoidDistanceM === 'number'
        ? { defaultAvoidDistanceM: body.defaultAvoidDistanceM }
        : {}),
      ...(Array.isArray(bbox) && bbox.length === 4
        ? {
            bboxMinLng: Number(bbox[0]),
            bboxMinLat: Number(bbox[1]),
            bboxMaxLng: Number(bbox[2]),
            bboxMaxLat: Number(bbox[3]),
          }
        : {}),
    });
    if (!res.city) throw new ApiError(404, 'NOT_FOUND', 'City not found');
    return mapCity(res.city);
  },
  /**
   * Takes a city out of the planner and out of the shared map and routing
   * data, without deleting it.
   *
   * `rebuildNeeded` is the half that is not instant: tiles and the basemap
   * are one artefact built from every extract at once, so the city leaves
   * them only when they are rebuilt without it.
   */
  unpublishCity: async (id: string): Promise<{ note: string; rebuildNeeded: boolean }> => {
    const res = await rpc(unpublishCityMethod, { id });
    return { note: res.note, rebuildNeeded: res.rebuildNeeded };
  },
  deleteCity: async (id: string): Promise<{ deleted: boolean; note: string }> => {
    const res = await rpc(deleteCityMethod, { id });
    return { deleted: res.deleted, note: res.note };
  },

  /**
   * Looks up and stores the city's real administrative outline, so imports
   * trim to the city rather than to the rectangle around it.
   */
  fetchBoundary: async (
    cityId: string, query?: string,
  ): Promise<{ matched: string; areaKm2: number; percentOfBbox: number }> => {
    const res = await rpc(fetchBoundaryMethod, { cityId, query: query ?? '' });
    return {
      matched: res.matched,
      areaKm2: res.areaKm2,
      percentOfBbox: res.percentOfBbox,
    };
  },

  importOsm: async (cityId: string, url: string): Promise<Job> =>
    enqueuedJob(await rpc(enqueueImportOsm, { cityId, url })),

  /**
   * The same import, from a file the operator already has.
   *
   * The server stages it exactly as it stages a download and records the same
   * job row, so what comes back is a Job like any other and the panel watches
   * it the same way. It is already finished by the time this resolves — the
   * upload IS the work — which is why the row is fetched rather than
   * invented: its log and its outcome are what the progress view draws.
   */
  uploadOsm: async (
    cityId: string, file: File, onProgress?: (fraction: number) => void,
  ): Promise<Job> => {
    const { jobId } = await upload<{ jobId: string }>(
      `/api/admin/cities/${cityId}/osm?filename=${encodeURIComponent(file.name)}`,
      file,
      onProgress,
    );
    const res = await rpc(getJob, { id: jobId });
    if (!res.job) throw new ApiError(404, 'NOT_FOUND', 'Job not found');

    return mapJob(res.job);
  },
  importPois: async (
    cityId: string, options: { categoryKeys?: string[]; preview?: boolean } = {},
  ): Promise<Job> =>
    enqueuedJob(await rpc(enqueueImportPois, {
      cityId,
      categoryKeys: options.categoryKeys ?? [],
      preview: options.preview ?? false,
    })),
  /**
   * Foursquare places for one city.
   *
   * No options: the extract is defined entirely by the city's bounding box,
   * and the category mapping lives in the database rather than the request.
   */
  importFsq: async (cityId: string): Promise<Job> =>
    enqueuedJob(await rpc(enqueueImportFsq, { cityId })),
  buildValhalla: async (cityId: string): Promise<Job> =>
    enqueuedJob(await rpc(enqueueBuildValhalla, { cityId })),
  buildOtp: async (cityId: string): Promise<Job> =>
    enqueuedJob(await rpc(enqueueBuildOtp, { cityId })),
  buildBasemap: async (cityId: string): Promise<Job> =>
    enqueuedJob(await rpc(enqueueBuildBasemap, { cityId })),
  verify: async (cityId: string): Promise<Job> =>
    enqueuedJob(await rpc(enqueueVerify, { cityId })),

  uploadGtfs: async (cityId: string, file: File): Promise<{ filename: string; size: string }> =>
    request(`/api/admin/cities/${cityId}/gtfs?filename=${encodeURIComponent(file.name)}`, {
      method: 'POST',
      body: file,
      headers: { 'content-type': 'application/zip' },
    }),

  jobs: async (cityId?: string): Promise<Job[]> => {
    const res = await rpc(listJobs, { cityId: cityId ?? '', limit: 0 });
    return res.jobs.map(mapJob);
  },
  job: async (id: string): Promise<Job> => {
    const res = await rpc(getJob, { id });
    if (!res.job) throw new ApiError(404, 'NOT_FOUND', 'Job not found');
    return mapJob(res.job);
  },
  cancelJob: async (id: string): Promise<Job> => {
    const res = await rpc(cancelJobMethod, { id });
    if (!res.job) throw new ApiError(404, 'NOT_FOUND', 'Job not found');
    return mapJob(res.job);
  },

  /** Runs a real provider search, so misconfiguration is visible. */
  testExternalSearch: async (): Promise<{
    available: boolean; reason?: string; results: unknown[];
  }> => {
    const res = await rpc(testExternalSearchMethod, {});
    // The probe runs a real provider search, so the results are the ones it
    // actually got back — the panel counts them to tell "reached the provider
    // and it found nothing" from "never reached it".
    return { available: res.available, reason: res.reason, results: res.results };
  },

  setSetting: async (key: string, value: unknown): Promise<Record<string, unknown>> => {
    // The jsonb value travels as its JSON encoding; the response is empty, so
    // the legacy settings-map return collapses to {}.
    await rpc(setSettingMethod, { key, valueJson: JSON.stringify(value) });
    return {};
  },

  restartEngine: async (service: string): Promise<{ restarted: string }> => {
    const res = await rpc(restartEngineMethod, { service });
    return { restarted: res.restarted };
  },

  /** Every style, active or not — narrowed to one city's own plus the global set. */
  routeStyles: (cityId?: string) =>
    get<AdminRouteStyle[]>(`/api/admin/route-styles${cityId ? `?cityId=${cityId}` : ''}`),
  // Create/Update carry the edited row as a JSON object whose keys mirror the
  // legacy adminApi field names; the handler whitelists what it writes. Every
  // taxonomy write returns only the affected row's id, so the panels refresh
  // their own lists after — none consume the returned row except categories,
  // which are rebuilt below from the id.
  createRouteStyle: async (body: Record<string, unknown>): Promise<{ id: string }> => {
    const res = await rpc(createRouteStyleMethod, { bodyJson: JSON.stringify(body) });
    return { id: res.id };
  },
  updateRouteStyle: async (id: string, body: Record<string, unknown>): Promise<{ id: string }> => {
    const res = await rpc(updateRouteStyleMethod, { id, bodyJson: JSON.stringify(body) });
    return { id: res.id };
  },
  deleteRouteStyle: async (id: string): Promise<{ deleted: boolean }> => {
    await rpc(deleteRouteStyleMethod, { id });
    return { deleted: true };
  },

  // The same Connect RPC api.themes() uses. This was REST against
  // /api/admin/themes, which no handler serves — the admin themes panel
  // showed "404 page not found" instead of its list.
  themes: async (): Promise<import('./types').RouteTheme[]> => {
    const res = await rpc(listRouteThemes, {});
    return res.themes.map(mapRouteTheme);
  },
  createTheme: async (body: Record<string, unknown>): Promise<{ id: string }> => {
    const res = await rpc(createThemeMethod, { bodyJson: JSON.stringify(body) });
    return { id: res.id };
  },
  updateTheme: async (id: string, body: Record<string, unknown>): Promise<{ id: string }> => {
    const res = await rpc(updateThemeMethod, { id, bodyJson: JSON.stringify(body) });
    return { id: res.id };
  },
  deleteTheme: async (id: string): Promise<{ deleted: boolean }> => {
    await rpc(deleteThemeMethod, { id });
    return { deleted: true };
  },

  updateCategory: async (id: string, body: Record<string, unknown>): Promise<Category> => {
    await rpc(updateCategoryMethod, { id, bodyJson: JSON.stringify(body) });
    return reloadCategory(id);
  },

  /**
   * A category an operator adds themselves.
   *
   * The seeded list is what OpenStreetMap tags, which is not the same as
   * what a service needs to plan around — a depot, a checkpoint, a
   * distribution point.
   */
  createCategory: async (body: Record<string, unknown>): Promise<Category> => {
    const res = await rpc(createCategoryMethod, { bodyJson: JSON.stringify(body) });
    return reloadCategory(res.id);
  },

  /** Refused while places are filed under it; the refusal says how many. */
  deleteCategory: async (id: string): Promise<{ deleted: boolean }> => {
    await rpc(deleteCategoryMethod, { id });
    return { deleted: true };
  },

  // The same Connect RPC the grouped panels use. This was a REST call to
  // /api/admin/category-groups, which no handler serves — a 404 waiting for
  // the first caller.
  categoryGroups: async (): Promise<CategoryGroup[]> => {
    const res = await rpc(listCategoryGroups, {});
    return res.categoryGroups.map(mapCategoryGroup);
  },
  createCategoryGroup: async (body: Record<string, unknown>): Promise<{ id: string }> => {
    const res = await rpc(createCategoryGroupMethod, { bodyJson: JSON.stringify(body) });
    return { id: res.id };
  },
  updateCategoryGroup: async (
    key: string, body: Record<string, unknown>,
  ): Promise<{ id: string }> => {
    const res = await rpc(updateCategoryGroupMethod, { id: key, bodyJson: JSON.stringify(body) });
    return { id: res.id };
  },
  deleteCategoryGroup: async (key: string): Promise<{ deleted: boolean }> => {
    await rpc(deleteCategoryGroupMethod, { id: key });
    return { deleted: true };
  },

  // NOTE: there used to be a streamJob() here (SSE against
  // /api/admin/jobs/{id}/stream), but that endpoint was never implemented on
  // this backend — see the package doc in backend/internal/jobs/service.go.
  // It always 404s, so it was silently doing nothing. JobProgress now polls
  // GetJob (adminApi.job) instead; add a real stream back here only once the
  // backend actually serves one.
};
